import type { Message, LocaleStrings, Agent } from '../types';
import { createElement } from '../utils/dom';
import { MessageList } from './MessageList';

export interface PastConversationCallbacks {
  onBack: () => void;
  onClose: () => void;
  onNewConversation: () => void;
  onLoadMessages: (ticketId: string, cursor?: string) => Promise<{
    messages: Message[];
    hasMore: boolean;
    cursor?: string;
  }>;
}

/**
 * Read-only view of a past conversation
 */
export class PastConversation {
  private element: HTMLElement | null = null;
  private avatarElement: HTMLElement | null = null;
  private titleElement: HTMLElement | null = null;
  private avatarUrl: string | undefined;
  private messageList: MessageList;
  private callbacks: PastConversationCallbacks;
  private strings: LocaleStrings;
  private currentTicketId: string | null = null;
  private cursor: string | undefined;
  private hasMore = false;
  private isLoadingMore = false;

  constructor(callbacks: PastConversationCallbacks, strings: LocaleStrings) {
    this.callbacks = callbacks;
    this.strings = strings;
    this.messageList = new MessageList();
  }

  setStrings(strings: LocaleStrings): void {
    this.strings = strings;
  }

  setAvatarUrl(url: string | undefined): void {
    this.avatarUrl = url;
    this.renderAvatar();
  }

  /**
   * Set agent info for message avatars (the AI-persona photo fallback when
   * no brand logo applies)
   */
  setAgent(agent: Agent): void {
    this.messageList.setAgent(agent);
  }

  /**
   * Set the brand logo shown as the avatar of the conversation's first message only
   */
  setBrandLogoUrl(url: string | undefined): void {
    this.messageList.setBrandLogoUrl(url);
  }

  private renderAvatar(): void {
    if (!this.avatarElement) return;
    if (this.avatarUrl) {
      this.avatarElement.innerHTML = `<img src="${this.avatarUrl}" alt="Agent" class="cevro-past-conversation__avatar-img" />`;
      this.avatarElement.style.display = '';
    } else {
      this.avatarElement.innerHTML = '';
      this.avatarElement.style.display = 'none';
    }
  }

  render(): HTMLElement {
    if (this.element) return this.element;

    this.element = createElement('div', {
      className: 'cevro-past-conversation',
    });

    // Header (will be populated on load)
    const header = createElement('div', {
      className: 'cevro-past-conversation__header',
    });

    const backButton = createElement('button', {
      className: 'cevro-past-conversation__back',
      'aria-label': 'Back',
    });
    backButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <polyline points="15 18 9 12 15 6"/>
      </svg>
    `;
    backButton.addEventListener('click', () => this.callbacks.onBack());

    // Agent avatar
    this.avatarElement = createElement('div', {
      className: 'cevro-past-conversation__avatar',
    });
    this.renderAvatar();

    // Title
    this.titleElement = createElement('h2', {
      className: 'cevro-past-conversation__title',
    });

    const closeButton = createElement('button', {
      className: 'cevro-past-conversation__close',
      'aria-label': 'Close',
    });
    closeButton.innerHTML = `
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    `;
    closeButton.addEventListener('click', () => this.callbacks.onClose());

    header.appendChild(backButton);
    header.appendChild(this.avatarElement);
    header.appendChild(this.titleElement);
    header.appendChild(closeButton);
    this.element.appendChild(header);

    // Messages area (read-only)
    const messagesArea = this.messageList.render();
    this.messageList.setScrollTopHandler(() => this.loadMoreMessages());
    this.element.appendChild(messagesArea);

    // New Conversation button
    const newConvButton = createElement('button', {
      className: 'cevro-past-conversation__new-conversation',
    }, [this.strings.newConversationButton]);
    newConvButton.addEventListener('click', () => this.callbacks.onNewConversation());

    // Footer (Powered by)
    const footer = createElement('div', {
      className: 'cevro-past-conversation__footer',
    });
    footer.innerHTML = `${this.strings.poweredBy.replace('Cevro AI', '<a href="https://cevro.ai" target="_blank" rel="noopener">Cevro AI</a>')}`;

    // Action panel wrapping button + powered-by (per design)
    const actionPanel = createElement('div', { className: 'cevro-action-panel' });
    actionPanel.appendChild(newConvButton);
    actionPanel.appendChild(footer);
    this.element.appendChild(actionPanel);

    return this.element;
  }

  /**
   * Load a past conversation
   */
  async loadConversation(ticketId: string, title: string): Promise<void> {
    if (this.titleElement) {
      this.titleElement.textContent = title;
    }
    // Reset pagination state
    this.currentTicketId = ticketId;
    this.cursor = undefined;
    this.hasMore = false;
    this.messageList.clear();

    try {
      // Load latest page (no cursor = most recent messages)
      const result = await this.callbacks.onLoadMessages(ticketId);

      // Guard: user may have switched to another conversation while we were loading
      if (this.currentTicketId !== ticketId) return;

      this.cursor = result.cursor;
      this.hasMore = result.hasMore;

      // Bulk load: no animation, no scroll per message
      for (const msg of result.messages) {
        this.messageList.addMessage(msg, false, true);
      }
      // Scroll to bottom — show latest messages
      this.messageList.scrollToBottom();
    } catch (error) {
      console.error('[Cevro] Failed to load conversation messages:', error);
    }
  }

  private async loadMoreMessages(): Promise<boolean> {
    if (!this.currentTicketId || !this.hasMore || this.isLoadingMore) return false;

    const requestedTicketId = this.currentTicketId;
    this.isLoadingMore = true;
    try {
      const result = await this.callbacks.onLoadMessages(
        requestedTicketId,
        this.cursor,
      );

      // Guard: user may have switched conversations while loading
      if (this.currentTicketId !== requestedTicketId) return false;

      this.cursor = result.cursor;
      this.hasMore = result.hasMore;

      // Prepend older messages at the top
      this.messageList.prependMessages(result.messages);
      return true;
    } catch (error) {
      console.error('[Cevro] Failed to load more messages:', error);
      return false;
    } finally {
      this.isLoadingMore = false;
      this.messageList.finishLoadingTop();
    }
  }

  show(): void {
    if (this.element) {
      this.element.style.display = 'flex';
    }
  }

  hide(): void {
    if (this.element) {
      this.element.style.display = 'none';
    }
  }

  destroy(): void {
    this.messageList.destroy();
    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element);
    }
    this.element = null;
    this.avatarElement = null;
    this.titleElement = null;
  }
}
